/**
 * The weekly scroll (docs/weekly.md, docs/roadmap-2026-10.md `weeklyUI`): written by the household.
 * From Friday 6 pm to the end of Saturday, local, anyone can write appreciations and
 * accomplishments; on Sunday at 5 am the server writes them onto the week's scroll. Pure: the week's
 * window and key, the writing window, the entries' rules, the scroll's sections and words, the
 * notice board's lead and the pushes' words. Shared by the server (server/weekly.ts) and both
 * clients. Also home to the Done board's other small rules (the gallery's view switch, the empty
 * board's line), the scroll's measures and when the weekly sheet shows, so both clients draw and
 * decide them alike.
 */

// ---------- Zones (Intl only, no server imports) ----------

export interface LocalParts {
  y: number;
  m: number;
  d: number;
  h: number;
  min: number;
}

const formatters = new Map<string, Intl.DateTimeFormat>();
function formatter(tz: string): Intl.DateTimeFormat {
  let f = formatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric" });
    formatters.set(tz, f);
  }
  return f;
}

/** Whether `tz` is a zone this runtime knows. */
export function isZone(tz: unknown): tz is string {
  if (typeof tz !== "string" || !tz) return false;
  try {
    formatter(tz);
    return true;
  } catch {
    return false;
  }
}

/** `instant`'s calendar date, hour and minute in `tz` (also used by shared/flash.ts's gold rush pick). */
export function localParts(instant: Date, tz: string): LocalParts {
  const parts: Record<string, number> = {};
  for (const p of formatter(tz).formatToParts(instant)) if (p.type !== "literal") parts[p.type] = Number(p.value);
  return { y: parts.year!, m: parts.month!, d: parts.day!, h: parts.hour! % 24, min: parts.minute! };
}

/** The UTC instant of local `y-m-d h:00` in `tz` (a skipped hour lands just after the gap). */
export function zonedInstant(y: number, m: number, d: number, h: number, tz: string): Date {
  const wanted = Date.UTC(y, m - 1, d, h, 0);
  let guess = wanted;
  for (let i = 0; i < 3; i++) {
    const p = localParts(new Date(guess), tz);
    const seen = Date.UTC(p.y, p.m - 1, p.d, p.h, p.min);
    if (seen === wanted) break;
    guess += wanted - seen;
  }
  return new Date(guess);
}

/** Monday 1 .. Sunday 7 for a calendar date. */
function isoWeekday(y: number, m: number, d: number): number {
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return wd === 0 ? 7 : wd;
}

export function addDays(y: number, m: number, d: number, days: number): { y: number; m: number; d: number } {
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
}

/** "2026-W40": the ISO week (Monday first, the week holding the year's first Thursday is W01) of `instant`'s date in `tz`. */
export function weekKey(instant: Date, tz: string): string {
  const { y, m, d } = localParts(instant, tz);
  const thursday = addDays(y, m, d, 4 - isoWeekday(y, m, d));
  const jan1 = Date.UTC(thursday.y, 0, 1);
  const week = Math.floor((Date.UTC(thursday.y, thursday.m - 1, thursday.d) - jan1) / 86_400_000 / 7) + 1;
  return `${thursday.y}-W${String(week).padStart(2, "0")}`;
}

/** A week in a household's zone: its key, local Monday 00:00 and the next Monday 00:00 (ISO UTC), and the Sunday 5 am the scroll is due. */
export interface WeekWindow {
  key: string;
  start: string;
  end: string;
  dueAt: string;
}

/** The hour on Sunday, household local time, the scroll is written from the week's entries and sent. */
export const WEEKLY_HOUR = 5;

/**
 * How long after Sunday 5 am the scroll may still be written. The sweep runs every few minutes, so
 * the first pass after five writes it; the window keeps a household's first pass after a deploy, or
 * after a quiet stretch with no cron, from writing a week long gone.
 */
export const WEEKLY_WINDOW_MS = 24 * 3_600_000;

/** The week (Monday to Sunday, in `tz`) that holds `instant`. */
export function weekWindow(instant: Date, tz: string): WeekWindow {
  const { y, m, d } = localParts(instant, tz);
  const monday = addDays(y, m, d, 1 - isoWeekday(y, m, d));
  const next = addDays(monday.y, monday.m, monday.d, 7);
  const sunday = addDays(monday.y, monday.m, monday.d, 6);
  return {
    key: weekKey(instant, tz),
    start: zonedInstant(monday.y, monday.m, monday.d, 0, tz).toISOString(),
    end: zonedInstant(next.y, next.m, next.d, 0, tz).toISOString(),
    dueAt: zonedInstant(sunday.y, sunday.m, sunday.d, WEEKLY_HOUR, tz).toISOString(),
  };
}

/**
 * The week whose scroll is due at `now`, or null: this week once its Sunday 5 am has passed, or last
 * week while its Sunday 5 am is less than WEEKLY_WINDOW_MS ago (a Monday-morning pass after a quiet
 * Sunday). Claimed once per household and week by the caller (server/weekly.ts).
 */
export function weeklyDue(now: Date, tz: string): WeekWindow | null {
  const t = now.getTime();
  const week = weekWindow(now, tz);
  if (t >= Date.parse(week.dueAt)) return t - Date.parse(week.dueAt) < WEEKLY_WINDOW_MS ? week : null;
  const last = weekWindow(new Date(Date.parse(week.start) - 3_600_000), tz);
  return t - Date.parse(last.dueAt) < WEEKLY_WINDOW_MS ? last : null;
}

// ---------- The writing window ----------

/**
 * The scroll is written by the household (owner, 2026-10-05: "Rather than the this week scroll be
 * auto generated, starting Friday evening until Saturday night, everyone should be able to write
 * down appreciations and accomplishments that will show up on the this week scroll Sunday at 5am").
 * The window opens Friday 18:00 and closes at the end of Saturday (Sunday 00:00, so Saturday
 * 23:59:59 is the last second), household local.
 *
 * The week key convention: an entry belongs to the ISO week (Monday to Sunday, local) that holds the
 * Friday and Saturday it was written on and the Sunday it is read on, so `weekKey` of any instant in
 * the window is the key of the scroll written that Sunday at 5 am (`weeklyDue`), and the sweep and
 * the window can never disagree.
 */
export const WRITING_OPENS = { weekday: 5, hour: 18 } as const;

/**
 * Where `now` sits in its week: "before" (Monday to Friday 18:00), "open" (Friday 18:00 to the end
 * of Saturday, writing allowed), "waiting" (Sunday 00:00 to 5 am, written and not yet read), "read"
 * (from Sunday 5 am, the scroll is written).
 */
export type WritingPhase = "before" | "open" | "waiting" | "read";

export interface WritingWindow {
  /** The week the window belongs to, "2026-W40": the week holding `now`. */
  week: string;
  /** Whether entries may be written now. */
  open: boolean;
  phase: WritingPhase;
  /** Friday 18:00, Sunday 00:00 (the close, exclusive) and Sunday 05:00 (the read), local, as UTC ISO. */
  opensAt: string;
  closesAt: string;
  readAt: string;
}

/** The writing window of the week holding `now`, in `tz`. */
export function writingWindow(now: Date, tz: string): WritingWindow {
  const w = weekWindow(now, tz);
  const monday = localParts(new Date(w.start), tz);
  const friday = addDays(monday.y, monday.m, monday.d, WRITING_OPENS.weekday - 1);
  const sunday = addDays(monday.y, monday.m, monday.d, 6);
  const opensAt = zonedInstant(friday.y, friday.m, friday.d, WRITING_OPENS.hour, tz).toISOString();
  const closesAt = zonedInstant(sunday.y, sunday.m, sunday.d, 0, tz).toISOString();
  const t = now.getTime();
  const phase: WritingPhase = t < Date.parse(opensAt) ? "before" : t < Date.parse(closesAt) ? "open" : t < Date.parse(w.dueAt) ? "waiting" : "read";
  return { week: w.key, open: phase === "open", phase, opensAt, closesAt, readAt: w.dueAt };
}

/** How long the workshop's "Open the scroll for writing" keeps the window open before it closes on its own. */
export const WRITE_OPEN_MS = 24 * 3_600_000;

/** GET and POST /api/weekly/write-open: when the workshop's opening closes on its own, or null while it is off. */
export interface WeeklyWriteOpenResponse {
  until: string | null;
}

/** POST /api/weekly/write-open: open (for 24 hours from now) or close. */
export interface WeeklyWriteOpenBody {
  open: boolean;
}

/**
 * The writing window the household actually gets: `real` (writingWindow), unless the workshop has
 * opened the scroll for writing until `openUntil` (an ISO instant; owner, 2026-10-05: "When I said I
 * needed a dev tool to test the scroll I meant I needed a way to place comments on it as though it
 * was Friday-Sunday"). While that instant is still ahead, the window is open now, closing at it, for
 * the week the scroll will be read on next: this week's, or once this week's has been read (Sunday
 * from 5 am) the next week's. A real window that is open anyway wins; a past or missing `openUntil`
 * changes nothing. Pure; the server's weekly routes are its callers (server/weekly.ts currentWindow).
 */
export function effectiveWindow(real: WritingWindow, openUntil: string | null | undefined, now: Date, tz: string): WritingWindow {
  const until = openUntil ? Date.parse(openUntil) : NaN;
  const t = now.getTime();
  if (real.open || !Number.isFinite(until) || until <= t) return real;
  // Monday 5 am of the next week: past any clock change, inside the next week.
  const base = real.phase === "read" ? writingWindow(new Date(Date.parse(real.readAt) + WEEKLY_WINDOW_MS), tz) : real;
  const opensAt = Date.parse(base.opensAt) <= t ? base.opensAt : now.toISOString();
  return { ...base, open: true, phase: "open", opensAt, closesAt: new Date(until).toISOString() };
}

// ---------- The entries ----------

export type WeeklyKind = "appreciation" | "accomplishment";
export const WEEKLY_KINDS: readonly WeeklyKind[] = ["appreciation", "accomplishment"];

export function isWeeklyKind(v: unknown): v is WeeklyKind {
  return v === "appreciation" || v === "accomplishment";
}

/** The longest an entry runs, in characters. Plain text, no photos. */
export const WEEKLY_ENTRY_MAX = 140;

/** One line on the scroll, as weekly_scrolls.data keeps it. */
export interface WeeklyLine {
  name: string;
  kind: WeeklyKind;
  text: string;
  created_at: string;
}

/** One entry as GET /api/weekly/entries lists it (weekly_entries). */
export interface WeeklyEntry extends WeeklyLine {
  id: string;
  week_key: string;
}

/**
 * What GET /api/weekly/entries answers: this week's key, its window, the entries so far in the order
 * written, and how many of them the caller wrote (`mine`), so a board knows the lines left
 * (linesLeft) without asking again.
 */
export interface WeeklyEntriesResponse {
  window: WritingWindow;
  entries: WeeklyEntry[];
  mine: number;
  /** The week's tally so far: from the last Sunday 5 am read to now (tallySpan). */
  summary: WeeklySummary;
}

export interface CreateWeeklyEntryBody {
  kind: WeeklyKind;
  text: string;
}

/** PATCH /api/weekly/entries/:id: a line changed in place; its kind stays unless one is given. */
export interface UpdateWeeklyEntryBody {
  kind?: WeeklyKind;
  text: string;
}

/**
 * The most lines one person writes on a week's scroll (owner, 2026-10-05: "Max of 5 scroll notes per
 * week, and notes cannot be removed only edited, to prevent abuse"). Counted from the stored lines,
 * so it holds across reloads and devices. A line once written stays; it can be changed, never taken off.
 */
export const WEEKLY_LINE_CAP = 5;

/** The lines a person has left this week, never below none. */
export function linesLeft(mine: number | null | undefined): number {
  return Math.max(0, WEEKLY_LINE_CAP - Math.max(0, Math.floor(Number(mine) || 0)));
}

/** In place of "Add yours" once a person's lines are written, and the refusal of one more. */
export const WEEKLY_CAP_REACHED = "Your five lines are written.";

/** The refusal of a change to someone else's line. Names nobody: the lines are anonymous. */
export const WEEKLY_NOT_YOURS = "Only its writer or a steward can change this line.";

/** What a scroll holds (weekly_scrolls.data): the week and its entries in the order written. */
export interface WeeklyData {
  week: string;
  start: string;
  end: string;
  /** Absent on a row written before the household wrote the scroll (it shows as a week nobody wrote on). */
  entries?: WeeklyLine[];
  /** The week's tally; absent on every row written before 2026-10-06, and then no tally shows. */
  summary?: WeeklySummary;
}

// ---------- The tally ----------

/** The week's numbers, household totals, nobody's name (owner, 2026-10-06: "The this week scroll should summarize how much gold was earned, how many tasks completed, how many chores completed, how many big tasks"). */
export interface WeeklySummary {
  /** The span counted, ISO: from the previous Sunday 5 am read to this one (every job lands on exactly one scroll). */
  from: string;
  to: string;
  /** Gold earned by the whole household in the span: the ledger's earned kinds (shared/ranks.ts EARNED_KINDS, net of reversals), never below 0. */
  gold: number;
  /** Done in the span: one-off tasks (schedule_id null), chores (schedule_id set), and big jobs (big_job = 1, counted among the other two as well). */
  tasks: number;
  chores: number;
  big: number;
}

/**
 * The span a scroll read at `readAt` (a Sunday 5 am) counts: from the previous week's Sunday 5 am to
 * `readAt`, so each job lands on exactly one scroll. Seven days back lands in the previous week
 * whatever the clock change. Pure.
 */
export function tallySpan(readAt: string, tz: string): { from: string; to: string } {
  return { from: weekWindow(new Date(Date.parse(readAt) - 7 * 86_400_000), tz).dueAt, to: readAt };
}

export type WeeklyTallyKey = "gold" | "tasks" | "chores" | "big";

/** The four figures' small labels, in the order they stand on the paper. */
export const WEEKLY_TALLY_LABELS: Readonly<Record<WeeklyTallyKey, string>> = { gold: "Gold", tasks: "Tasks", chores: "Chores", big: "Big jobs" };

const TALLY_KEYS: readonly WeeklyTallyKey[] = ["gold", "tasks", "chores", "big"];

/** A whole number, never below 0. */
const whole = (n: number) => Math.max(0, Math.round(Number(n) || 0));

/** A figure as the tally writes it: a whole number, never below 0, with a thousands separator ("1,240"), no plus sign. */
function tallyFigure(n: number): string {
  return whole(n).toLocaleString("en-US");
}

/** The big jobs' label with projectsUI on (docs/projects.md; shared/projects.ts PROJECT_WORDS.plural, kept here so this module imports nothing). */
export const WEEKLY_PROJECTS_LABEL = "Projects";

/**
 * The tally's four cells: gold, tasks, chores, big jobs, each a figure over its label. With `projects`
 * (projectsUI, docs/projects.md) the big jobs' label reads "Projects".
 */
export function weeklyTally(summary: WeeklySummary, projects = false): { key: WeeklyTallyKey; value: string; label: string }[] {
  return TALLY_KEYS.map((key) => ({ key, value: tallyFigure(summary[key]), label: key === "big" && projects ? WEEKLY_PROJECTS_LABEL : WEEKLY_TALLY_LABELS[key] }));
}

const counted = (n: number, one: string, many: string) => `${tallyFigure(n)} ${whole(n) === 1 ? one : many}`;

/** The tally for a screen reader, one sentence: "340 gold earned, 7 tasks, 12 chores and 2 big jobs done." ("2 projects" with `projects`.) */
export function weeklyTallyLabel(summary: WeeklySummary, projects = false): string {
  const big = projects ? counted(summary.big, "project", "projects") : counted(summary.big, "big job", "big jobs");
  return `${tallyFigure(summary.gold)} gold earned, ${counted(summary.tasks, "task", "tasks")}, ${counted(summary.chores, "chore", "chores")} and ${big} done.`;
}

/** A stored or answered tally if it is well formed (four finite numbers, the span as strings), else null: a row written before 2026-10-06 has none. */
export function scrollSummary(data: { summary?: unknown } | null | undefined): WeeklySummary | null {
  const s = data?.summary as Partial<WeeklySummary> | null | undefined;
  if (!s || typeof s !== "object" || typeof s.from !== "string" || typeof s.to !== "string") return null;
  if (!TALLY_KEYS.every((k) => typeof s[k] === "number" && Number.isFinite(s[k]))) return null;
  return { from: s.from, to: s.to, gold: s.gold!, tasks: s.tasks!, chores: s.chores!, big: s.big! };
}

/** The latest scroll, as GET /api/weekly answers it. */
export interface WeeklyScroll {
  week: string;
  computed_at: string;
  data: WeeklyData;
}

export interface WeeklyResponse {
  scroll: WeeklyScroll | null;
}

/** An entry's text as stored: trimmed, runs of whitespace (new lines too) as one space. */
export function cleanEntryText(raw: string): string {
  return raw.replace(/\s+/g, " ").trim();
}

/** Why an entry cannot be written, in plain words, or null when it will do (the server's check; the composer's is form-missing's). */
export function entryProblem(body: { kind?: unknown; text?: unknown }): string | null {
  if (!isWeeklyKind(body.kind)) return "Pick appreciation or accomplishment.";
  const text = typeof body.text === "string" ? cleanEntryText(body.text) : "";
  if (!text) return "Write a line first.";
  if (Array.from(text).length > WEEKLY_ENTRY_MAX) return "That line is too long.";
  return null;
}

/** The refusal outside the window. */
export const WEEKLY_CLOSED = "The scroll is open for writing Friday evening to Saturday night.";

/** A scroll's lines, whatever row it came from: an old numeric row (no entries) reads as no lines. */
export function scrollLines(data: Partial<WeeklyData> | null | undefined): WeeklyLine[] {
  const list = (data as { entries?: unknown } | null | undefined)?.entries;
  if (!Array.isArray(list)) return [];
  return list.filter((e): e is WeeklyLine => !!e && typeof e === "object" && typeof e.name === "string" && typeof e.text === "string" && isWeeklyKind(e.kind));
}

/** The card's heading. */
export const WEEKLY_CARD_TITLE = "This week";

/** A heading over each kind's lines, in the title's style; a heading shows only when it has lines. */
export const WEEKLY_HEADINGS: Readonly<Record<WeeklyKind, string>> = { appreciation: "Appreciations", accomplishment: "Accomplishments" };

/** The one line of a week nobody wrote on. */
export const WEEKLY_EMPTY = "Nobody wrote on the scroll this week.";

export interface WeeklySection<L extends WeeklyLine = WeeklyLine> {
  kind: WeeklyKind;
  heading: string;
  lines: L[];
}

/** The appreciations, then the accomplishments, each in the order written; a kind with no lines is left out. */
export function weeklySections<L extends WeeklyLine>(lines: readonly L[]): WeeklySection<L>[] {
  return WEEKLY_KINDS.map((kind) => ({ kind, heading: WEEKLY_HEADINGS[kind], lines: lines.filter((l) => l.kind === kind) })).filter((s) => s.lines.length > 0);
}

/**
 * A line as the scroll shows it and a screen reader hears it: the words alone. The lines are
 * anonymous (owner, 2026-10-05: "The notes should be anonymous, not show who wrote them too"); the
 * writer is kept only so the server knows whose line may be changed and how many lines each wrote.
 */
export function weeklyLineText(l: Pick<WeeklyLine, "text">): string {
  return l.text;
}

const sentence = (s: string) => (/[.!?]$/.test(s) ? s : `${s}.`);

/** The scroll's name for a screen reader: the title, the tally when there is one, then each heading and its lines, or the empty week's line. */
export function weeklyLabel(lines: readonly WeeklyLine[], summary?: WeeklySummary | null, projects = false): string {
  const sections = weeklySections(lines);
  const tally = summary ? [weeklyTallyLabel(summary, projects)] : [];
  if (!sections.length) return [WEEKLY_CARD_TITLE, ...tally, WEEKLY_EMPTY].map(sentence).join(" ");
  return [WEEKLY_CARD_TITLE, ...tally, ...sections.flatMap((s) => [s.heading, ...s.lines.map(weeklyLineText)])].map(sentence).join(" ");
}

// ---------- Writing on it ----------

/** The small ghost button at the foot of the paper while the window is open. */
export const WEEKLY_ADD = "Add yours";
/** The composer's primary. */
export const WEEKLY_SUBMIT = "Write it";
/** The composer's primary when it is changing a line already written. */
export const WEEKLY_SAVE = "Save it";
/**
 * The small ink pencil on the lines this person may change (their own; a steward's, every one), in
 * the place and footprint the x had: 12x12 art pixels drawn at 24 in the paper's lighter ink
 * (SCROLL_INK.quiet), tip at the bottom left, its end at the top right. Both clients draw it from
 * these rows, an "X" a pixel; no asset.
 */
export const SCROLL_PENCIL: readonly string[] = [
  "............",
  "........XX..",
  ".......XXXX.",
  "......X.XXX.",
  ".....XXX.X..",
  "....XXXX....",
  "...XXXX.....",
  "..XXXX......",
  ".XXXX.......",
  ".XXX........",
  ".XX.........",
  "............",
];

/** The pencil's inked pixels as [x, y] art pixels, for both clients' drawing. */
export function pencilPixels(): [number, number][] {
  return SCROLL_PENCIL.flatMap((row, y) => [...row].flatMap((c, x) => (c === "X" ? [[x, y] as [number, number]] : [])));
}
export const WEEKLY_KIND_OPTIONS: readonly { value: WeeklyKind; label: string }[] = [
  { value: "appreciation", label: "Appreciation" },
  { value: "accomplishment", label: "Accomplishment" },
];
export const WEEKLY_PLACEHOLDER: Readonly<Record<WeeklyKind, string>> = { appreciation: "Thanks Hue for...", accomplishment: "What you did" };

/**
 * Gold for a line (docs/weekly.md "Gold for a line"; owner, 2026-10-05: "Posting an appreciation or
 * accomplishment yields 10 gold"). The amount is shared/bounty.ts GOLD.scrollLine, paid once when a
 * line is written and never on a change (a line is never taken off, so nothing comes back); these
 * are the words: the ledger's `title` for the line's gold (what the purse shows), and the board's
 * toast after "Write it" ("+10 Written on the scroll.").
 */
export const SCROLL_GOLD_TITLE = "Wrote on the scroll";
export const SCROLL_GOLD_TOAST = "Written on the scroll.";

/**
 * What leads the open board's notice board (docs/weekly.md "On the board"): while the window is open,
 * this week's scroll in progress with "Add yours"; from Saturday night until the scroll is written
 * (Sunday 5 am), the same without it; otherwise the newest finished scroll once this device has seen
 * it in its sheet; else nothing.
 */
export type WeeklyLead = { kind: "writing"; week: string; canAdd: boolean } | { kind: "finished"; scroll: WeeklyScroll } | null;

export function weeklyLead(window: WritingWindow | null, finished: WeeklyScroll | null, seen: string | null): WeeklyLead {
  if (window && (window.phase === "open" || window.phase === "waiting")) return { kind: "writing", week: window.week, canAdd: window.open };
  // Sunday from 5 am, before a sweep has written this week's: still the scroll in progress, closed.
  if (window && window.phase === "read" && (!finished || finished.week < window.week)) return { kind: "writing", week: window.week, canAdd: false };
  return finished && weeklyOnBoard(seen, finished) ? { kind: "finished", scroll: finished } : null;
}

/**
 * The workshop's "Show the scroll now" (owner, 2026-10-05: "I need a way to test out the weekly
 * scroll with dev tools hidden in workshop"): the scroll as it would read if it were written now,
 * from this week's lines so far (none: the empty week's line). Shown on this device only, never
 * bookmarked, nothing posted.
 */
export const WEEKLY_PREVIEW_LABEL = "Show the scroll now";

export function previewScroll(current: Pick<WeeklyEntriesResponse, "window" | "entries"> & { summary?: WeeklySummary | null }, now: Date): WeeklyScroll {
  const week = current.window.week;
  const entries = current.entries.map(({ name, kind, text, created_at }) => ({ name, kind, text, created_at }));
  // The tally so far, as the entries route counted it (from the last Sunday's read to now).
  const summary = scrollSummary(current);
  return { week, computed_at: now.toISOString(), data: { week, start: "", end: "", entries, ...(summary ? { summary } : {}) } };
}

// ---------- The pushes ----------

/** Both pushes' title. */
export const WEEKLY_PUSH_TITLE = "The weekly scroll";
/** Sunday 5 am, when the week has at least one entry: a few plain words, no numbers. */
export const WEEKLY_READ_BODY = "Read what the house wrote.";
/** Friday 6 pm, when the window opens. */
export const WEEKLY_WRITE_BODY = "Write an appreciation or an accomplishment by Saturday night.";

/** The push's body: "read" on Sunday, "write" on Friday. */
export function weeklyPushBody(which: "read" | "write"): string {
  return which === "read" ? WEEKLY_READ_BODY : WEEKLY_WRITE_BODY;
}

// ---------- The scroll itself ----------

/**
 * The scroll's pieces (scripts/make-scroll.mjs), in art pixels, drawn at SCROLL_SCALE CSS pixels or
 * points each on both clients: the roller across the top and the bottom is a strip with a cap at each
 * end and a middle that stretches; the knobs stand `overhang` past the paper on each side; the paper
 * tucks `tuck` rows under each roller's empty rows. The paper is ink-edged at one art pixel.
 */
export const SCROLL_ROLLER = { w: 48, h: 12, cap: 12, overhang: 3, tuck: 2 } as const;
/** The paper's tile, repeated under the writing. */
export const SCROLL_PAPER = 32;
export const SCROLL_SCALE = 2;
/** The writing: ink on paper, no shadow. The title in brown ink, the lines in dark ink, the labels, the empty week and the x in the lighter quiet ink; the paper's edge. */
export const SCROLL_INK = { title: "#7a3d0a", line: "#4a2c12", quiet: "#8a5a2b", edge: "#120c07" } as const;

// ---------- The weekly sheet: once a week, then the notice board ----------

/**
 * The newest scroll comes up once on the next open of the app each week, before What's new, and then
 * lives on the open board's notice board (owner, 2026-10-05: "The weekly scroll should come up on
 * next view of the app each week, similar to the what's new, and it should take priority. After seen
 * it is on the notice board."). The device bookmarks the newest week it has shown under this key.
 */
export const WEEKLY_SEEN_KEY = "op.weeklySeen";

const WEEK_KEY = /^\d{4}-W\d{2}$/;

/** A stored bookmark as a week key, or null for none (or anything unreadable). */
export function parseWeeklySeen(raw: string | null | undefined): string | null {
  const v = raw?.trim() ?? "";
  return WEEK_KEY.test(v) ? v : null;
}

/** The later of two week keys; week keys sort as text ("2026-W09" < "2026-W40" < "2027-W01"). */
export function laterWeek(a: string | null, b: string | null): string | null {
  if (a === null) return b;
  if (b === null) return a;
  return a >= b ? a : b;
}

/** Whether `scroll` is newer than the device's bookmark. A device with no bookmark is decided by weeklyOnOpen. */
export function weeklySheetDue(seen: string | null, scroll: WeeklyScroll | null): boolean {
  return !!scroll && seen !== null && scroll.week > seen;
}

/**
 * What an open does with the newest scroll, as What's new does with its entries: a brand new device
 * (no bookmark, never used) bookmarks the week silently and shows nothing; a device used before with
 * no bookmark shows it once; any other shows it when it is newer than the bookmark. What shows is
 * bookmarked on close.
 */
export function weeklyOnOpen(seen: string | null, existing: boolean, scroll: WeeklyScroll | null): { show: WeeklyScroll | null; bookmarkNow: string | null } {
  if (!scroll) return { show: null, bookmarkNow: null };
  if (seen === null && !existing) return { show: null, bookmarkNow: scroll.week };
  if (seen === null) return { show: scroll, bookmarkNow: null };
  return { show: weeklySheetDue(seen, scroll) ? scroll : null, bookmarkNow: null };
}

/** Whether the open board's notice board carries `scroll`: once this device has seen its week (or a later one). */
export function weeklyOnBoard(seen: string | null, scroll: WeeklyScroll | null): boolean {
  return !!scroll && seen !== null && seen >= scroll.week;
}

/**
 * Whether the sheet shows now: it has a scroll, the board has loaded, nothing holds it (the workshop,
 * a tour, the first open's welcome) and What's new is not already up (that is never cut short).
 */
export function weeklySheetShows(scroll: WeeklyScroll | null, boardLoaded: boolean, held: boolean, whatsNewUp: boolean): boolean {
  return !!scroll && boardLoaded && !held && !whatsNewUp;
}

/** Whether What's new waits for the weekly scroll: while the scroll is due (being read, or still to show) or showing, unless What's new was up first. */
export function whatsNewWaitsForWeekly(weeklyDue: boolean, whatsNewUp: boolean): boolean {
  return weeklyDue && !whatsNewUp;
}

// ---------- The Done gallery (`galleryUI`) ----------

/** The device's choice of Done view, remembered under this key on both clients. */
export const DONE_VIEW_KEY = "op.doneView";

export type DoneView = "list" | "gallery";

export const DONE_VIEW_OPTIONS: readonly { value: DoneView; label: string }[] = [
  { value: "list", label: "List" },
  { value: "gallery", label: "Gallery" },
];

/** A stored value read back: anything but "gallery" is the list. */
export function parseDoneView(raw: string | null | undefined): DoneView {
  return raw === "gallery" ? "gallery" : "list";
}

/** The gallery's tiles: the done tasks with an after photo, in the board's order (newest first). */
export function galleryTasks<T extends { status: string; done_photo_id: string | null }>(tasks: readonly T[]): T[] {
  return tasks.filter((t) => t.status === "done" && !!t.done_photo_id);
}

/** A thumbnail's name for a screen reader. */
export function galleryLabel(task: { title: string; done_by: string | null }): string {
  return task.done_by ? `${task.title}, done by ${task.done_by}` : task.title;
}

/** The gallery with no after photo among what is loaded. */
export const GALLERY_EMPTY = "No after photos yet.";

/**
 * The key of BoardSummary.looks: a peon's name in lower case, so "hue" and "Hue" are one peon, as
 * the look itself is matched (server/db.ts getAppearance). Shared so the server's keys and the
 * clients' lookups can never disagree.
 */
export function lookKey(name: string): string {
  return name.toLowerCase();
}

/**
 * The look a gallery tile draws in its corner (owner, 2026-10-05: "In gallery, a small version of the
 * peon who did it should be in the corner"): whoever did the task, from the summary's `looks`; the
 * default look (an empty one: brown, bald, nothing worn) for a peon who never chose; null when
 * nobody is named, and then no peon is drawn.
 */
export function doerLook<L extends object>(looks: Readonly<Record<string, L>> | null | undefined, doneBy: string | null | undefined): L | Partial<L> | null {
  if (!doneBy) return null;
  return looks?.[lookKey(doneBy)] ?? {};
}

/**
 * The tile's peon: the mascot with room for his hair, 46x42 art pixels (the 46x44 layer canvas less
 * its two empty bottom rows), the mascot's 38 rows on the box's foot and every layer's top on the
 * box's top, at two thirds of a CSS pixel (a point) an art pixel: about 31x28, and two device pixels
 * an art pixel on a 3x phone (the pin icon's rule), so he stays crisp and the photo stays the point.
 */
export const GALLERY_PEON = { w: 46, h: 42, mascotH: 38, layerH: 44, scale: 2 / 3 } as const;

// ---------- The empty board (`peonMoodsUI`) ----------

/** The one line under the sleeping peon when nothing at all is open. */
export const EMPTY_BOARD_LINE = "All done. Nothing to do.";

/** The empty board scene's frames (scripts/make-empty-board.mjs): 120x80 each, side by side; the still frame is the last. */
export const EMPTY_BOARD_W = 120;
export const EMPTY_BOARD_H = 80;
export const EMPTY_BOARD_FRAMES = 3;
/** How long each frame of the "z z z" holds: hard frames, no easing. */
export const EMPTY_BOARD_FRAME_MS = 700;

// ---------- The crown up close ----------

/** How long a press on the Peon of the Month card must hold to open the winner's look large. */
export const CROWN_LONG_PRESS_MS = 500;

/** The crowned portrait's size in art pixels (46x44) and the viewer's scale. */
export const CROWN_VIEW_SCALE = 5;
